import { logDataModels } from '../dataModelsLogger';
import { emptyGroup } from './conditionNodes';
import { defaultRule, emptySubject, emptyTarget, emptyWindow, hasExceptAudience, policyDefinitionMode, ruleDefinitionMode } from './engine';
import { policyRecordId } from './policyPath';
import { looksLikeMaskCase } from './parser';
import { currentPolicyExpression } from './queryTest';
import type {
  ConditionNode,
  GovernancePolicy,
  MaskAction,
  PolicyAsset,
  PolicyDefinitionMode,
  PolicyDocument,
  PolicyDocumentAppliesTo,
  PolicyDocumentDefinition,
  PolicyDocumentMask,
  PolicyDocumentMaskRule,
  PolicyDocumentOwner,
  PolicyDocumentWho,
  PolicyRule,
  PolicyScope,
  PolicyTarget,
  SubjectSelector,
} from './types';
import { POLICY_DOCUMENT_SCHEMA } from './types';

export interface PolicyDocumentArgs {
  path: string;
  assets: PolicyAsset[];
  complexity: PolicyDefinitionMode;
}

function present<T>(value: T | undefined | null | ''): value is T {
  return value !== undefined && value !== null && value !== '';
}

function hasCondition(node: ConditionNode | undefined): node is ConditionNode {
  if (!node) return false;
  if (node.kind === 'predicate') return true;
  return node.children.some((child) => hasCondition(child));
}

function assetName(assets: PolicyAsset[], id: string): string {
  return assets.find((asset) => asset.id === id)?.name ?? id;
}

function toWho(subject: SubjectSelector): PolicyDocumentWho {
  if (subject.mode === 'users') return { mode: 'users', userIds: [...subject.userIds] };
  if (subject.mode === 'roles') return { mode: 'roles', roles: [...subject.roles] };
  if (subject.mode === 'groups') return { mode: 'groups', groups: [...subject.groups] };
  if (subject.mode === 'attributes') {
    return {
      mode: 'attributes',
      key: subject.attribute.key,
      operator: subject.attribute.operator,
      value: subject.attribute.value,
    };
  }
  if (subject.mode === 'expression') return { mode: 'expression', expression: subject.expression };
  return { mode: 'everyone' };
}

function fromWho(who: PolicyDocumentWho | undefined): SubjectSelector {
  const base = emptySubject();
  if (!who || who.mode === 'everyone') return base;
  if (who.mode === 'users') return { ...base, mode: 'users', userIds: [...who.userIds] };
  if (who.mode === 'roles') return { ...base, mode: 'roles', roles: [...who.roles] };
  if (who.mode === 'groups') return { ...base, mode: 'groups', groups: [...who.groups] };
  if (who.mode === 'attributes') {
    return { ...base, mode: 'attributes', attribute: { key: who.key, operator: who.operator, value: who.value } };
  }
  return { ...base, mode: 'expression', expression: who.expression };
}

function toMask(mask: MaskAction): PolicyDocumentMask {
  const next: PolicyDocumentMask = { kind: mask.kind };
  if (present(mask.value)) next.value = mask.value;
  if (mask.n !== undefined) next.n = mask.n;
  if (present(mask.expression)) next.expression = mask.expression;
  return next;
}

function fromMask(mask: PolicyDocumentMask): MaskAction {
  return {
    kind: mask.kind,
    value: mask.value,
    n: mask.n,
    expression: mask.expression,
  };
}

function toOwner(scope: PolicyScope): PolicyDocumentOwner {
  const owner: PolicyDocumentOwner = { level: scope.level, workspaceId: scope.vaultId };
  if (scope.modelId) owner.modelId = scope.modelId;
  if (scope.assetId) owner.tableId = scope.assetId;
  return owner;
}

function toAppliesTo(policy: GovernancePolicy, assets: PolicyAsset[]): PolicyDocumentAppliesTo {
  const appliesTo: PolicyDocumentAppliesTo = { mode: policy.target.mode };
  if (policy.target.mode === 'selected') {
    appliesTo.assets = policy.target.assetIds.map((id) => ({ id, name: assetName(assets, id) }));
  } else if (policy.target.mode === 'current' && policy.scope.assetId) {
    appliesTo.assets = [{ id: policy.scope.assetId, name: assetName(assets, policy.scope.assetId) }];
  } else if (policy.target.mode === 'dynamic') {
    const match = policy.target.match;
    if (match.combinator !== 'AND') appliesTo.combinator = match.combinator;
    if (match.classifications.length) appliesTo.classifications = [...match.classifications];
    if (match.tags.length) appliesTo.tags = [...match.tags];
    if (present(match.columnName)) appliesTo.columnName = match.columnName;
    if (present(match.columnPattern)) appliesTo.columnPattern = match.columnPattern;
    if (match.dataTypes.length) appliesTo.dataTypes = [...match.dataTypes];
  }
  if (policy.target.columnRefs.length) {
    appliesTo.columns = policy.target.columnRefs.map((ref) => ({
      assetId: ref.assetId,
      asset: assetName(assets, ref.assetId),
      column: ref.column,
    }));
  }
  return appliesTo;
}

function toDefinition(policy: GovernancePolicy, complexity: PolicyDefinitionMode): PolicyDocumentDefinition {
  const expression = currentPolicyExpression(policy).trim() || undefined;
  const first = policy.rules[0];
  if (policy.type === 'ROW_LEVEL') {
    const rules = (policy.rules.length ? policy.rules : first ? [first] : [defaultRule('ROW_LEVEL')]).map((rule) => {
      const next: { who: PolicyDocumentWho; condition?: ConditionNode; when?: string; mode?: PolicyDefinitionMode } = {
        who: toWho(rule.subject),
        mode: ruleDefinitionMode(rule),
      };
      if (hasCondition(rule.condition)) next.condition = rule.condition;
      if (present(rule.expression)) next.when = rule.expression;
      return next;
    });
    const definition: PolicyDocumentDefinition = {
      kind: 'row_filter',
      mode: policyDefinitionMode(policy),
      who: rules[0]?.who ?? toWho(emptySubject()),
      rules,
    };
    if (rules[0]?.condition) definition.condition = rules[0].condition;
    return definition;
  }
  if (policy.type === 'COLUMN_LEVEL') {
    return {
      kind: 'column_mask',
      mode: policyDefinitionMode(policy),
      rules: policy.rules.map((rule) => {
        const mask = rule.action.type === 'COLUMN' ? rule.action.mask : { kind: 'none' as const };
        const caseSql = ruleDefinitionMode(rule) === 'complex' && present(rule.expression) && looksLikeMaskCase(rule.expression)
          ? rule.expression
          : '';
        const next: PolicyDocumentMaskRule = {
          who: toWho(rule.subject),
          mask: toMask(caseSql ? { kind: 'custom_expression', expression: caseSql, value: caseSql } : mask),
          mode: ruleDefinitionMode(rule),
        };
        if (hasExceptAudience(rule.except)) next.except = toWho(rule.except);
        if (ruleDefinitionMode(rule) === 'complex') {
          if (present(rule.expression) && !caseSql) next.when = rule.expression;
        } else if (hasCondition(rule.condition)) {
          next.condition = rule.condition;
        }
        return next;
      }),
      ...(expression ? { expression } : {}),
    };
  }
  if (policy.type === 'ACCESS_CONTROL') {
    return {
      kind: 'access',
      mode: complexity,
      rules: policy.rules.map((rule) => {
        const access = rule.action.type === 'ACCESS'
          ? { who: toWho(rule.subject), effect: rule.action.effect, operations: [...rule.action.operations] }
          : { who: toWho(rule.subject), effect: 'allow' as const, operations: ['SELECT' as const] };
        return present(rule.expression) ? { ...access, when: rule.expression } : access;
      }),
      ...(expression ? { expression } : {}),
    };
  }
  if (policy.type === 'TIME_BASED') {
    return {
      kind: 'time',
      mode: complexity,
      rules: policy.rules.map((rule) => {
        const action = rule.action.type === 'TIME' ? rule.action : { effect: 'expire' as const, window: emptyWindow() };
        const next = { who: toWho(rule.subject), effect: action.effect, window: { ...action.window } };
        return present(rule.expression) ? { ...next, when: rule.expression } : next;
      }),
      ...(expression ? { expression } : {}),
    };
  }
  const classifications = policy.target.match.classifications;
  return {
    kind: 'sensitive',
    mode: complexity,
    ...(classifications.length ? { classifications: [...classifications] } : {}),
    rules: policy.rules.map((rule) => {
      const mask = rule.action.type === 'SENSITIVE' || rule.action.type === 'COLUMN' ? rule.action.mask : { kind: 'full_string' as const };
      const next = { who: toWho(rule.subject), mask: toMask(mask) };
      return present(rule.expression) ? { ...next, when: rule.expression } : next;
    }),
    ...(expression ? { expression } : {}),
  };
}

export function toPolicyDocument(policy: GovernancePolicy, args: PolicyDocumentArgs): PolicyDocument {
  const key = policy.policyKey.trim();
  const document: PolicyDocument = {
    schema: POLICY_DOCUMENT_SCHEMA,
    path: args.path,
    id: policy.id || policyRecordId(policy),
    key: key || 'untitled',
    name: policy.name,
    type: policy.type,
    status: policy.status,
    owner: toOwner(policy.scope),
    appliesTo: toAppliesTo(policy, args.assets),
    definition: toDefinition(policy, args.complexity),
    meta: {
      version: policy.version,
      generatedId: Boolean(policy.generateId),
      createdAt: policy.createdAt,
      updatedAt: policy.updatedAt,
    },
  };
  if (present(policy.description?.trim())) document.description = policy.description?.trim();
  return document;
}

function fromAppliesTo(appliesTo: PolicyDocumentAppliesTo, owner: PolicyDocumentOwner): PolicyTarget {
  const target = emptyTarget();
  target.mode = appliesTo.mode;
  if (appliesTo.assets?.length) target.assetIds = appliesTo.assets.map((asset) => asset.id);
  else if (appliesTo.mode === 'current' && owner.tableId) target.assetIds = [owner.tableId];
  if (appliesTo.columns?.length) {
    target.columnRefs = appliesTo.columns.map((column) => ({ assetId: column.assetId, column: column.column }));
  }
  if (appliesTo.combinator) target.match.combinator = appliesTo.combinator;
  if (appliesTo.classifications?.length) target.match.classifications = [...appliesTo.classifications];
  if (appliesTo.tags?.length) target.match.tags = [...appliesTo.tags];
  if (present(appliesTo.columnName)) target.match.columnName = appliesTo.columnName;
  if (present(appliesTo.columnPattern)) target.match.columnPattern = appliesTo.columnPattern;
  if (appliesTo.dataTypes?.length) target.match.dataTypes = [...appliesTo.dataTypes];
  return target;
}

function fromDefinition(doc: PolicyDocument): { type: GovernancePolicy['type']; rules: PolicyRule[]; advancedExpression: string } {
  const definition = doc.definition;
  const advancedExpression = definition.expression ?? '';
  if (definition.kind === 'row_filter') {
    const rows = definition.rules?.length
      ? definition.rules
      : [{ who: definition.who ?? { mode: 'everyone' as const }, condition: definition.condition, when: definition.expression, mode: definition.mode }];
    const rules = rows.map((rule, index) => {
      const expression = rule.when?.trim() || '';
      const definitionModeValue: PolicyDefinitionMode =
        rule.mode === 'simple' || rule.mode === 'complex'
          ? rule.mode
          : expression
            ? 'complex'
            : 'simple';
      return {
        ...defaultRule('ROW_LEVEL'),
        id: `rule-${index + 1}`,
        subject: fromWho(rule.who),
        condition: rule.condition ?? (definitionModeValue === 'simple' ? emptyGroup(`rule-${index + 1}`) : undefined),
        expression,
        definitionMode: definitionModeValue,
      };
    });
    const legacyExpression = advancedExpression.trim();
    if (legacyExpression && !rules.some((rule) => rule.expression?.trim())) {
      const target = rules.find((rule) => rule.definitionMode === 'complex') ?? rules[0];
      if (target) {
        target.expression = legacyExpression;
        target.definitionMode = 'complex';
      }
    }
    return {
      type: 'ROW_LEVEL',
      advancedExpression: '',
      rules,
    };
  }
  if (definition.kind === 'column_mask') {
    return {
      type: 'COLUMN_LEVEL',
      advancedExpression,
      rules: definition.rules.map((rule, index) => {
        const mask = fromMask(rule.mask);
        const caseSql = mask.kind === 'custom_expression' && looksLikeMaskCase(mask.expression || mask.value || '')
          ? (mask.expression || mask.value || '').trim()
          : '';
        const expression = rule.when?.trim() || caseSql;
        const definitionModeValue: PolicyDefinitionMode =
          rule.mode === 'simple' || rule.mode === 'complex'
            ? rule.mode
            : expression
              ? 'complex'
              : 'simple';
        return {
          id: `rule-${index + 1}`,
          subject: fromWho(rule.who),
          action: { type: 'COLUMN' as const, mask },
          ...(rule.except ? { except: fromWho(rule.except) } : {}),
          condition: rule.condition ?? (definitionModeValue === 'simple' ? emptyGroup(`rule-${index + 1}`) : undefined),
          expression,
          definitionMode: definitionModeValue,
        };
      }),
    };
  }
  if (definition.kind === 'access') {
    return {
      type: 'ACCESS_CONTROL',
      advancedExpression,
      rules: definition.rules.map((rule, index) => ({
        id: `rule-${index + 1}`,
        subject: fromWho(rule.who),
        action: { type: 'ACCESS', effect: rule.effect, operations: [...rule.operations] },
        expression: rule.when,
      })),
    };
  }
  if (definition.kind === 'time') {
    return {
      type: 'TIME_BASED',
      advancedExpression,
      rules: definition.rules.map((rule, index) => ({
        id: `rule-${index + 1}`,
        subject: fromWho(rule.who),
        action: { type: 'TIME', effect: rule.effect, window: { ...emptyWindow(), ...rule.window } },
        expression: rule.when,
      })),
    };
  }
  return {
    type: 'SENSITIVE_DATA',
    advancedExpression,
    rules: definition.rules.map((rule, index) => ({
      id: `rule-${index + 1}`,
      subject: fromWho(rule.who),
      action: { type: 'SENSITIVE', mask: fromMask(rule.mask) },
      expression: rule.when,
    })),
  };
}

export function fromPolicyDocument(doc: PolicyDocument): GovernancePolicy {
  if (doc.schema !== POLICY_DOCUMENT_SCHEMA) {
    logDataModels('warning', 'governance_policy_document_schema_unknown', { schema: String(doc.schema ?? '') });
  }
  const hydrated = fromDefinition(doc);
  const target = fromAppliesTo(doc.appliesTo, doc.owner);
  if (hydrated.type === 'SENSITIVE_DATA' && doc.definition.kind === 'sensitive' && doc.definition.classifications?.length) {
    target.match.classifications = [...doc.definition.classifications];
  }
  return {
    id: doc.id,
    name: doc.name,
    description: doc.description ?? '',
    type: doc.type || hydrated.type,
    status: doc.status,
    scope: {
      level: doc.owner.level,
      vaultId: doc.owner.workspaceId,
      modelId: doc.owner.modelId,
      assetId: doc.owner.tableId,
    },
    target,
    rules: hydrated.rules.length ? hydrated.rules : [defaultRule(doc.type || hydrated.type)],
    advancedExpression: hydrated.advancedExpression,
    policyKey: doc.key,
    generateId: Boolean(doc.meta.generatedId),
    version: doc.meta.version,
    createdAt: doc.meta.createdAt,
    updatedAt: doc.meta.updatedAt,
  };
}

export function formatPolicyJson(policy: GovernancePolicy, args: PolicyDocumentArgs): string {
  return JSON.stringify(toPolicyDocument(policy, args), null, 2);
}

export function definitionMode(document?: PolicyDocument): PolicyDefinitionMode {
  return document?.definition?.mode === 'complex' ? 'complex' : 'simple';
}

export function documentFromApi(value: unknown): PolicyDocument | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const doc = value as PolicyDocument;
  if (!doc.definition || typeof doc.definition !== 'object' || !doc.definition.kind) return undefined;
  if (doc.schema && doc.schema !== POLICY_DOCUMENT_SCHEMA) {
    logDataModels('warning', 'governance_policy_document_schema_unknown', { schema: String(doc.schema) });
  }
  return doc;
}

export function policyDocumentPath(policy: GovernancePolicy): string {
  const owner = policy.scope.assetId || policy.scope.modelId || policy.scope.vaultId;
  return `${owner}:${policy.policyKey || 'untitled'}`;
}
